import { makeAutoObservable } from "mobx";

import { faGear, faHome, faTriangleExclamation, faUser } from "@fortawesome/pro-regular-svg-icons";

import { registeredFeatures } from "../features";
import { translationKeys } from "../models/app-translation-keys";
import { RouteConstants } from "../models/enums/route-constants.enum";
import { IMenuItem } from "../models/interfaces/menu-item.interface";

export enum ExternalLinkKeys {
  Contact = "contact",
  PrivacyPolicity = "privacyPolicy",
  Impressum = "impressum",
}

interface IExternalLink {
  key: ExternalLinkKeys;
  labelKey: string;
  url: string | undefined;
  icon?: any;
}

export class NavigationStore {
  public landingPage: IMenuItem = {
    id: "landingPage",
    label: translationKeys.pages?.landingPage.title ?? "",
    icon: faHome,
    url: "/landing",
    path: RouteConstants.LandingPage,
    showFooter: true,
    hasSideBar: false,
    fontAwesomeIcon: faHome,
  };

  public adminAreaPage: IMenuItem = {
    id: "adminArea",
    label: translationKeys.pages?.adminArea.title ?? "",
    icon: faGear,
    url: RouteConstants.AdminArea,
    path: RouteConstants.AdminArea,
    showFooter: true,
    hasSideBar: false,
    fontAwesomeIcon: faGear,
  };

  public userSettingsAreaPage: IMenuItem = {
    id: "userSettingsArea",
    label: translationKeys.pages?.userSettingsArea.title ?? "",
    icon: faUser,
    url: RouteConstants.UserSettingsArea,
    path: RouteConstants.UserSettingsArea,
    showFooter: true,
    hasSideBar: false,
    fontAwesomeIcon: faUser,
  };

  public pageNotFound: IMenuItem = {
    id: "pageNotFound",
    label: translationKeys.pages?.pageNotFound.title ?? "",
    url: RouteConstants.PageNotFound,
    path: RouteConstants.PageNotFound,
    showFooter: true,
    hasSideBar: false,
    fontAwesomeIcon: faTriangleExclamation,
  };

  private readonly _menuItems: IMenuItem[] = [
    this.landingPage,
    this.adminAreaPage,
    this.userSettingsAreaPage,
    this.pageNotFound,
    ...registeredFeatures.map(f => f.menuItem),
  ];

  private _externalNavigationLinks?: IExternalLink[];
  private _currentActiveMenuItem: IMenuItem | undefined;

  constructor() {
    makeAutoObservable(this);
  }

  public set externalNavigationLinks(links: IExternalLink[]) {
    this._externalNavigationLinks = links.slice();
  }

  public setExternalNavigationLinkURL = (key: ExternalLinkKeys, url: string) => {
    this._externalNavigationLinks = this._externalNavigationLinks
      ?.map(link => {
        if (link.key === key) {
          return { ...link, url };
        }
        return link;
      })
      .slice();
  };

  public get externalNavigationLinks(): IExternalLink[] | undefined {
    return this._externalNavigationLinks;
  }

  public setActiveMenuItem = (menuItem: IMenuItem | undefined) => {
    this._currentActiveMenuItem = menuItem;
  };

  public setActiveMenuItemBasedOnPath = (path: string | undefined) => {
    const activeMenuItem = this._menuItems.find(item => path?.replace("/", "").includes(item.path ?? ""));
    this.setActiveMenuItem(activeMenuItem ?? this.landingPage);
  };

  public get currentActiveMenuItem(): IMenuItem | undefined {
    return this._currentActiveMenuItem;
  }
}
